import React, { useEffect, useState } from 'react';
import CreatableSelect from 'react-select/creatable';

import CommonImage from '@/components/CommonImage';
import {
	convertFileToBase64,
	formatSelectedIssueToEdit,
	formatSelectOptions,
	INITIAL_STATE_FOR_CREATE_ISSUE,
	isIssueCreator,
	isNotifyUser,
	isResponsiblePerson,
	ISSUE_STATUS,
	validateForm
} from '@/features/design/utils/designUtils';
import { useGetAllUserQuery, useGetEnumsQuery, useGetProductStageListQuery } from '@/api/commonApis';
import {
	useAddIssueDocumentsMutation,
	useCreateIssueMutation,
	useRemoveIssueDocumentMutation,
	useUpdateIssueMutation
} from '@/features/design/api/designDetailsApi';
import { toastError, toastSuccess } from '@/components/Toast';
import { getUserInfo } from '@/utils/Util';
import LoaderComponent from '@/components/LoaderComponent';
import Textarea from '@/components/Textarea';

const CreateAndSolveIssuePanel = ({
	onClose,
	productId,
	selectedIssueToEdit,
	setSelectedIssueToEdit,
	setActiveIssueIndex
}) => {
	const [inputValues, setInputValues] = useState(INITIAL_STATE_FOR_CREATE_ISSUE);
	const [errors, setErrors] = useState({});
	const userInfo = getUserInfo();

	const { data: userList } = useGetAllUserQuery({
		params: { page: 0, size: 1000 }
	});
	const { data: issuePlaceList } = useGetEnumsQuery({
		path: 'info.nitex.app.product.IssueType'
	});
	const { data: productStageList = [] } = useGetProductStageListQuery();
	const [createIssue, { isLoading: issueCreating }] = useCreateIssueMutation();
	const [updateIssue, { isLoading: issueUpdating }] = useUpdateIssueMutation();
	const [removeIssueDocument, { isLoading: issueRemoving }] = useRemoveIssueDocumentMutation();
	const [addIssueDocuments, { isLoading: imgUploading }] = useAddIssueDocumentsMutation();

	useEffect(() => {
		if (selectedIssueToEdit) {
			setInputValues(formatSelectedIssueToEdit(selectedIssueToEdit));
		}
	}, [selectedIssueToEdit]);

	const handleChange = (key, value) => {
		setInputValues((prev) => ({
			...prev,
			[key]: value
		}));

		setErrors((prev) => ({
			...prev,
			[key]: ''
		}));
	};

	const handleFileChange = (e) => {
		const files = Array.from(e.target.files);
		setInputValues((prev) => ({
			...prev,
			file: [...prev.file, ...files]
		}));
	};

	const handleRemoveFile = (img) => {
		const updatedImages = inputValues?.file?.filter((item) => item?.name !== img?.name);
		setInputValues((prev) => ({
			...prev,
			file: updatedImages
		}));
	};

	const handleCheckboxChange = (e) => {
		handleChange('isHoldTechpack', e.target.checked);
	};

	const handleSuccess = () => {
		toastSuccess('Issue updated successfully');
		setInputValues(INITIAL_STATE_FOR_CREATE_ISSUE);
		onClose();
	};

	const handleRemoveUploadedImg = (doc) => {
		removeIssueDocument({ issueId: selectedIssueToEdit?.id, params: { documentId: doc?.id } })
			.unwrap()
			.then(() => {
				const updatedDocs = selectedIssueToEdit?.documentResponses?.filter(
					(item) => item?.id !== doc?.id
				);

				setSelectedIssueToEdit((prev) => ({
					...prev,
					documentResponses: updatedDocs
				}));
				toastSuccess('Image deleted successfully');
			})
			.catch((error) => {
				toastError(error?.data?.message || 'Something went wrong!');
			});
	};

	const handleUploadImage = async (e) => {
		const selectedImages = Array.from(e.target.files);
		const convertedImages = await convertFileToBase64(selectedImages);
		addIssueDocuments({ issueId: selectedIssueToEdit?.id, body: convertedImages })
			.unwrap()
			.then(() => {
				setInputValues((prev) => ({
					...prev,
					file: [...prev.file, ...selectedImages]
				}));
				toastSuccess('Image uploaded');
			})
			.catch((error) => {
				toastError(error?.data?.messae || 'Something went wrong!');
			});
	};

	const createRequestBody = async () => {
		const body = {
			productId,
			holdDevelopment: inputValues.isHoldTechpack,
			rework: inputValues.isReWorkNeeded,
			reDevelopment: inputValues.isRedevelopment
		};
		if (inputValues.issueDetails?.trim()) {
			body.details = inputValues.issueDetails;
		}

		if (inputValues.file.length > 0 && !selectedIssueToEdit) {
			body.documentDTOList = await convertFileToBase64(inputValues.file);
		}

		if (inputValues.issuePlace?.length > 0) {
			body.issueTypeList = inputValues.issuePlace.map((item) => item?.value);
		}

		if (inputValues.responsiblePerson?.length > 0) {
			body.responsibleUsersList = inputValues.responsiblePerson.map((item) => item?.id);
		}

		if (inputValues.notifyTo?.length > 0) {
			body.notifyUsersList = inputValues.notifyTo.map((item) => item?.id);
		}

		if (inputValues.isReWorkNeeded && inputValues.reworkTaskStages?.length > 0) {
			body.reworkStages = inputValues.reworkTaskStages.map((item) => item?.id);
		}

		return body;
	};

	const handleSubmit = async () => {
		const isValid = validateForm(inputValues, setErrors);
		if (!isValid) {
			return;
		}

		try {
			const body = await createRequestBody();

			if (selectedIssueToEdit) {
				updateIssue({
					issueId: selectedIssueToEdit?.id,
					body: { ...body, status: inputValues.issueStatus.id }
				})
					.unwrap()
					.then(() => {
						setActiveIssueIndex(0);
						handleSuccess();
					})
					.catch((error) => {
						toastError(error?.data?.message || 'Something went wrong');
					});
			} else {
				createIssue({
					body,
					issueId: selectedIssueToEdit?.id
				})
					.unwrap()
					.then(() => {
						handleSuccess();
					})
					.catch((error) => {
						toastError(error?.data?.message || 'Something went wrong');
					});
			}
		} catch {
			toastError('Error processing file. Please try again.');
		}
	};

	// const handleDownloadImage = async (url) => {
	// 	try {
	// 		const response = await fetch(url?.docUrl);
	// 		const blob = await response.blob();
	// 		const blobUrl = window.URL.createObjectURL(blob);

	// 		const link = document.createElement('a');
	// 		link.href = blobUrl;
	// 		link.download = url?.name;
	// 		document.body.appendChild(link);
	// 		link.click();
	// 		document.body.removeChild(link);
	// 	} catch {
	// 		toastError('Failed to download image');
	// 	}
	// };

	return (
		<LoaderComponent loading={issueCreating || issueUpdating || issueRemoving || imgUploading}>
			<div className="create-issue-sidebar bg-white shadow-4dp">
				<div className="create-issue-sidebar-header d-flex justify-content-between align-items-center">
					<h3 className="semibold-16 mb-0">
						{selectedIssueToEdit ? 'Solve an issue' : 'Raise an issue'}
					</h3>
					<CommonImage
						className="cursor-pointer"
						alt="close"
						src="/icons/close.svg"
						onClick={onClose}
					/>
				</div>
				<div className="create-issue-sidebar-body scroll-y-label">
					<div className="row">
						<div className="col-md-12">
							<div className="form-group">
								<div className="sort-button d-flex justify-content-start align-items-center">
									<label className="w-auto semibold-14 gray_dark_02">Issue Status :</label>

									<div className="select-component ml-2">
										<div
											className="click-box status-box click mb-1 pl-2 p-0"
											data-toggle={selectedIssueToEdit ? 'dropdown' : ''}
										>
											<span className={`status ${inputValues.issueStatus?.className}`}>
												{inputValues.issueStatus?.value}
											</span>
											{selectedIssueToEdit && (
												<CommonImage src="/images/chevron_down.svg" />
											)}
										</div>
										<div className="dropdown-box dropdown-menu">
											<div className="list-items scroll-y-label">
												{ISSUE_STATUS?.map((status) => (
													<div
														className="single-item"
														onClick={() => handleChange('issueStatus', status)}
													>
														<p>{status?.value}</p>
													</div>
												))}
											</div>
										</div>
									</div>
								</div>
							</div>
						</div>
						<div className="col-md-12">
							<div className="upload-img mb-2 gap-2 d-flex justify-content-start align-items-start">
								<div className="file-uploader height-100 upload-design-image mb-4 mb-sm-0">
									<label
										className={`drag-upload m-0 ${selectedIssueToEdit && !isIssueCreator(userInfo, selectedIssueToEdit) ? 'cursor-none' : ''}`}
										htmlFor="drag-upload"
									/>
									<input
										accept="image/*"
										className="file-upload"
										name="image"
										type="file"
										id="drag-upload"
										onChange={selectedIssueToEdit ? handleUploadImage : handleFileChange}
										disabled={
											selectedIssueToEdit &&
											(isNotifyUser(userInfo, selectedIssueToEdit) ||
												isResponsiblePerson(userInfo, selectedIssueToEdit))
										}
										multiple
									/>
								</div>
								<div className="all-img-preview d-flex align-items-center gap-2 scroll-x-label pb-2">
									{inputValues.file?.length > 0 && (
										<>
											{inputValues?.file?.map((img) => (
												<div
													className="upload-img-preview position-relative ml-2"
													key={img?.name}
												>
													<CommonImage
														src={URL.createObjectURL(img)}
														alt="edit"
														isStatic={false}
													/>

													<div
														className="action-icon position-absolute top-2 right-2 cursor-pointer"
														onClick={() => handleRemoveFile(img)}
													>
														<CommonImage
															width="20px"
															src="/icons/delete_gray.svg"
															alt="delete"
														/>
													</div>
												</div>
											))}
										</>
									)}
									{selectedIssueToEdit && (
										<>
											{selectedIssueToEdit?.documentResponses?.map((doc) => (
												<div
													className="upload-img-preview position-relative ml-2"
													key={doc?.id}
												>
													<CommonImage
														src={doc?.docUrl}
														alt="edit"
														isStatic={false}
														// onClick={() => handleDownload(doc)}
													/>
													{selectedIssueToEdit &&
														isIssueCreator(userInfo, selectedIssueToEdit) && (
															<div
																className="action-icon position-absolute top-2 right-2 cursor-pointer"
																onClick={() => handleRemoveUploadedImg(doc)}
															>
																<CommonImage
																	width="20px"
																	src="/icons/delete_gray.svg"
																	alt="delete"
																/>
															</div>
														)}
												</div>
											))}
										</>
									)}
								</div>
							</div>
						</div>
						<div className="col-md-12">
							<div className="form-group no-shadow-react-select">
								<label htmlFor="collection">Issue type*</label>
								<CreatableSelect
									isMulti
									value={inputValues.issuePlace}
									onChange={(value) => handleChange('issuePlace', value)}
									options={formatSelectOptions(issuePlaceList)}
									isDisabled={
										selectedIssueToEdit && !isIssueCreator(userInfo, selectedIssueToEdit)
									}
									isValidNewOption={() => false}
								/>
								{errors?.issuePlace && <p className="error">{errors?.issuePlace}</p>}
							</div>
						</div>
						<div className="col-md-12">
							<div className="form-group">
								<label htmlFor="collection">Issue details*</label>
								<Textarea
									rows="1"
									name="issueDetails"
									id="issueDetails"
									value={inputValues.issueDetails}
									onChange={(e) => handleChange('issueDetails', e.target.value)}
									disabled={
										selectedIssueToEdit && !isIssueCreator(userInfo, selectedIssueToEdit)
									}
									className={`${selectedIssueToEdit && !isIssueCreator(userInfo, selectedIssueToEdit) ? 'cursor-none' : ''}`}
								/>
								{errors?.issueDetails && <p className="error">{errors?.issueDetails}</p>}
							</div>
						</div>
						<div className="col-md-12">
							<div className="form-group no-shadow-react-select">
								<label htmlFor="collection">Who will solve?*</label>
								<CreatableSelect
									isMulti
									value={inputValues.responsiblePerson}
									onChange={(value) => handleChange('responsiblePerson', value)}
									options={formatSelectOptions(userList)}
									isDisabled={
										selectedIssueToEdit && !isIssueCreator(userInfo, selectedIssueToEdit)
									}
									isValidNewOption={() => false}
								/>
								{errors?.responsiblePerson && (
									<p className="error">{errors?.responsiblePerson}</p>
								)}
							</div>
						</div>
						<div className="col-md-12">
							<div className="form-group no-shadow-react-select">
								<label htmlFor="collection">Notify to</label>
								<CreatableSelect
									isMulti
									value={inputValues.notifyTo}
									onChange={(value) => handleChange('notifyTo', value)}
									options={formatSelectOptions(userList)}
									isDisabled={
										selectedIssueToEdit && !isIssueCreator(userInfo, selectedIssueToEdit)
									}
									isValidNewOption={() => false}
								/>
							</div>
						</div>
						<div className="col-md-12">
							<div className="custom-chekbox">
								<div className="form-group">
									<input
										type="checkbox"
										id="NITEX_DEVELOPMENT"
										name="isRedevelopment"
										checked={inputValues.isRedevelopment}
										onChange={(e) => handleChange('isRedevelopment', e.target.checked)}
										disabled={
											selectedIssueToEdit &&
											!isIssueCreator(userInfo, selectedIssueToEdit)
										}
									/>
									<label
										htmlFor="NITEX_DEVELOPMENT"
										className=" mb-0 gray_dark_01 regular-14"
									>
										Need redevelopment
									</label>
								</div>
							</div>
						</div>
						<div className="col-md-12">
							<div className="custom-chekbox">
								<div className="form-group">
									<input
										type="checkbox"
										id="NITEX_DEVELOPMENT2"
										name="isReWorkNeeded"
										checked={inputValues.isReWorkNeeded}
										onChange={(e) => handleChange('isReWorkNeeded', e.target.checked)}
										disabled={
											selectedIssueToEdit &&
											!isIssueCreator(userInfo, selectedIssueToEdit)
										}
									/>
									<label
										htmlFor="NITEX_DEVELOPMENT2"
										className="mb-0 gray_dark_01 regular-14"
									>
										Sample correction/Rework needed
									</label>
								</div>
							</div>
						</div>
						{inputValues.isReWorkNeeded && (
							<div className="col-md-12">
								<div className="form-group no-shadow-react-select">
									<label htmlFor="collection">Rework these task stage*</label>
									<CreatableSelect
										isMulti
										value={inputValues.reworkTaskStages}
										onChange={(value) => handleChange('reworkTaskStages', value)}
										options={formatSelectOptions(productStageList)}
										isClearable
										isDisabled={
											selectedIssueToEdit &&
											!isIssueCreator(userInfo, selectedIssueToEdit)
										}
										isValidNewOption={() => false}
									/>
									{errors?.reworkTaskStages && (
										<p className="error">{errors?.reworkTaskStages}</p>
									)}
								</div>
							</div>
						)}
						<div className="col-md-12">
							<div className="custom-chekbox">
								<div className="form-group">
									<input
										type="checkbox"
										id="isHoldTechpack"
										name="isHoldTechpack"
										checked={inputValues.isHoldTechpack}
										onChange={handleCheckboxChange}
										className="custom-checkbox-input"
										disabled={
											selectedIssueToEdit &&
											!isIssueCreator(userInfo, selectedIssueToEdit)
										}
									/>
									<label
										className="regular-14 d-flex gray_dark_01 align-items-center"
										htmlFor="isHoldTechpack"
									>
										<span>Hold this techpack until this issue is solved</span>
									</label>
								</div>
							</div>
						</div>
					</div>
				</div>
				<div className="modal-footer">
					<div className="footer-buttons text-right">
						<button className="button outline size32 ml-2" onClick={onClose}>
							Cancel
						</button>
						<button
							className="button size32 ml-2"
							onClick={handleSubmit}
							disabled={issueCreating || issueUpdating}
						>
							Update
						</button>
					</div>
				</div>
			</div>
		</LoaderComponent>
	);
};

export default CreateAndSolveIssuePanel;
